/**
 * Live GitHub pull requests.
 *
 * This is a static site (Parcel -> GitHub Pages), so there is no server to
 * proxy through: we talk to the public GitHub API straight from the browser and
 * keep a short-lived localStorage cache. That cache is not just a speed-up, it
 * is the rate-limit budget — unauthenticated GitHub allows ~10 search req/min
 * and 60 core req/hr per IP, and it doubles as the offline fallback when the
 * API is unreachable.
 */

const CONFIG = {
  username: "saud5150",
  // Restrict to specific repos, e.g. ["pytorch/pytorch", "ml-explore/mlx"].
  // Empty array = every public repo the user has opened a PR against.
  repos: [],
  // false = authored only. true = authored + PRs reviewed by the user.
  includeReviewed: false,
  count: 3,
  // How long a successful response stays authoritative.
  cacheTtlMs: 15 * 60 * 1000,
  // How long a stale cache may still be shown as a fallback when GitHub fails.
  cacheMaxAgeMs: 7 * 24 * 60 * 60 * 1000,
  // +additions / -deletions cost one extra core API call per PR (3 total).
  // Purely additive: if it fails the cards render fine without it.
  fetchDiffStats: true,
  requestTimeoutMs: 8000,
};

const API = "https://api.github.com";
const CACHE_VERSION = 1;
const CACHE_KEY = "sk.github.prs.v" + CACHE_VERSION;

const STATE_META = {
  merged: { label: "Merged", verb: "merged" },
  open: { label: "Open", verb: "updated" },
  closed: { label: "Closed", verb: "closed" },
};

/* ------------------------------------------------------------------ utils */

const el = (tag, className, text) => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
};

function relativeTime(iso) {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "";
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return minutes + "m ago";
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return hours + "h ago";
  const days = Math.floor(hours / 24);
  if (days < 30) return days + "d ago";
  const months = Math.floor(days / 30);
  if (months < 12) return months + "mo ago";
  return Math.floor(months / 12) + "y ago";
}

function absoluteTime(iso) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

/* ------------------------------------------------------------------ cache */

function readCache() {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    if (!cached || !Array.isArray(cached.items) || !cached.fetchedAt)
      return null;
    if (cached.signature !== configSignature()) return null;
    return cached;
  } catch (err) {
    return null;
  }
}

function writeCache(items) {
  try {
    window.localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({
        items,
        fetchedAt: Date.now(),
        signature: configSignature(),
      })
    );
  } catch (err) {
    /* private mode / quota — the page works fine without a cache */
  }
}

function configSignature() {
  return [
    CONFIG.username,
    CONFIG.repos.join(","),
    CONFIG.includeReviewed ? "r" : "a",
    CONFIG.count,
  ].join("|");
}

const cacheAge = (cached) => Date.now() - cached.fetchedAt;
const isFresh = (cached) => cacheAge(cached) < CONFIG.cacheTtlMs;
const isUsable = (cached) => cacheAge(cached) < CONFIG.cacheMaxAgeMs;

/* ------------------------------------------------------------------- data */

async function getJSON(url) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), CONFIG.requestTimeoutMs);
  try {
    const response = await fetch(url, {
      signal: controller.signal,
      headers: { Accept: "application/vnd.github+json" },
    });
    if (response.status === 403 || response.status === 429) {
      const error = new Error("GitHub rate limit reached");
      error.code = "rate-limit";
      throw error;
    }
    if (!response.ok) {
      throw new Error("GitHub responded " + response.status);
    }
    return await response.json();
  } finally {
    clearTimeout(timer);
  }
}

function searchQuery(role) {
  const parts = ["is:pr", role + ":" + CONFIG.username];
  // Repeated qualifiers of the same kind are OR'd by GitHub search.
  CONFIG.repos.forEach((repo) => parts.push("repo:" + repo));
  if (role === "reviewed-by") parts.push("-author:" + CONFIG.username);
  return parts.join(" ");
}

function searchUrl(role) {
  return (
    API +
    "/search/issues?q=" +
    encodeURIComponent(searchQuery(role)) +
    "&sort=updated&order=desc&per_page=30"
  );
}

function normalize(item) {
  const pr = item.pull_request || {};
  const merged = Boolean(pr.merged_at);
  const state = merged ? "merged" : item.state === "open" ? "open" : "closed";
  return {
    id: item.id,
    repo: item.repository_url.split("/repos/")[1] || "",
    number: item.number,
    title: item.title,
    url: item.html_url,
    state,
    draft: Boolean(item.draft),
    // The timestamp the card actually reports on, per state.
    stampedAt:
      (merged && pr.merged_at) ||
      (state === "closed" && item.closed_at) ||
      item.updated_at,
    updatedAt: item.updated_at,
  };
}

const TIER = { merged: 0, open: 1, closed: 2 };

/**
 * Prefer merged, fall back to open, then closed; drafts only if we would
 * otherwise come up short. Recency (most recently updated) breaks ties within
 * each tier.
 */
function selectPullRequests(all) {
  const byPreference = (a, b) =>
    TIER[a.state] - TIER[b.state] ||
    new Date(b.updatedAt) - new Date(a.updatedAt);

  const ready = all.filter((pr) => !pr.draft).sort(byPreference);
  const selected = ready.slice(0, CONFIG.count);

  if (selected.length < CONFIG.count) {
    const drafts = all.filter((pr) => pr.draft).sort(byPreference);
    selected.push(...drafts.slice(0, CONFIG.count - selected.length));
  }
  return selected;
}

async function fetchPullRequests() {
  const roles = CONFIG.includeReviewed ? ["author", "reviewed-by"] : ["author"];
  const responses = await Promise.all(roles.map((r) => getJSON(searchUrl(r))));

  const seen = new Set();
  const all = [];
  responses.forEach((payload) => {
    (payload.items || []).forEach((item) => {
      if (seen.has(item.id)) return;
      seen.add(item.id);
      all.push(normalize(item));
    });
  });

  return selectPullRequests(all);
}

/** Best-effort diff stats. Never allowed to fail the section. */
async function attachDiffStats(pullRequests) {
  if (!CONFIG.fetchDiffStats) return pullRequests;

  await Promise.all(
    pullRequests.map(async (pr) => {
      try {
        const detail = await getJSON(
          API + "/repos/" + pr.repo + "/pulls/" + pr.number
        );
        if (typeof detail.additions === "number") {
          pr.additions = detail.additions;
          pr.deletions = detail.deletions;
        }
      } catch (err) {
        /* stats are a bonus, not a requirement */
      }
    })
  );
  return pullRequests;
}

/* ------------------------------------------------------------------- view */

const SELECTORS = {
  root: "[data-pr-panel]",
  list: "[data-pr-list]",
  liveDot: "[data-pr-live]",
  liveLabel: "[data-pr-live-label]",
  refreshed: "[data-pr-refreshed]",
  viewAll: "[data-pr-view-all]",
};

function profileUrl() {
  return "https://github.com/" + CONFIG.username;
}

function searchPageUrl() {
  // GitHub's own PR search links use "+" for the spaces between qualifiers.
  const query = encodeURIComponent("is:pr author:" + CONFIG.username).replace(
    /%20/g,
    "+"
  );

  // Use public code search instead of /pulls dashboard (which 404s for logged-out visitors)
  return "https://github.com/search?q=" + query + "&type=pullrequests";
}

function renderSkeleton(list) {
  list.setAttribute("aria-busy", "true");
  list.replaceChildren();
  for (let i = 0; i < CONFIG.count; i += 1) {
    const row = el("div", "pr-card pr-card--skeleton");
    row.setAttribute("aria-hidden", "true");
    row.append(
      el("span", "pr-skel pr-skel--repo"),
      el("span", "pr-skel pr-skel--title"),
      el("span", "pr-skel pr-skel--meta")
    );
    list.append(row);
  }
}

function renderMessage(list, { text, linkLabel, linkHref, tone }) {
  list.setAttribute("aria-busy", "false");
  list.replaceChildren();
  const box = el("div", "pr-notice" + (tone ? " pr-notice--" + tone : ""));
  box.append(el("p", "pr-notice__text", text));
  if (linkLabel) {
    const link = el("a", "pr-notice__link", linkLabel);
    link.href = linkHref;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    box.append(link);
  }
  list.append(box);
}

function buildDiff(pr) {
  const diff = el("span", "pr-card__diff");
  diff.append(
    el("span", "pr-card__add", "+" + pr.additions),
    el("span", "pr-card__del", "\u2212" + pr.deletions)
  );
  diff.setAttribute(
    "aria-label",
    pr.additions + " additions, " + pr.deletions + " deletions"
  );
  return diff;
}

/**
 * Fold late-arriving diff stats into the cards already on screen. Re-rendering
 * the list here would replay the row-in animation, so patch in place instead.
 */
function applyDiffStats(list, pullRequests) {
  const cards = list.querySelectorAll(".pr-card");
  pullRequests.forEach((pr, index) => {
    const card = cards[index];
    if (!card || typeof pr.additions !== "number") return;
    const bottom = card.querySelector(".pr-card__bottom");
    if (!bottom || bottom.querySelector(".pr-card__diff")) return;
    bottom.append(buildDiff(pr));
  });
}

function renderCard(pr) {
  const meta = STATE_META[pr.state];

  const card = el("a", "pr-card");
  card.href = pr.url;
  card.target = "_blank";
  card.rel = "noopener noreferrer";
  card.setAttribute(
    "aria-label",
    pr.repo +
      " pull request #" +
      pr.number +
      ", " +
      meta.label +
      ": " +
      pr.title
  );

  // Row 1 — repo + state badge
  const top = el("div", "pr-card__top");
  const repo = el("span", "pr-card__repo");
  const [owner, name] = pr.repo.split("/");
  repo.append(
    el("span", "pr-card__owner", owner + "/"),
    el("span", "pr-card__name", name)
  );

  const badge = el("span", "pr-badge pr-badge--" + pr.state, meta.label);
  if (pr.draft) badge.append(el("span", "pr-badge__draft", "draft"));
  top.append(repo, badge);

  // Row 2 — title + number
  const title = el("h4", "pr-card__title", pr.title);
  title.title = pr.title;

  // Row 3 — number, time, diff stats
  const bottom = el("div", "pr-card__bottom");
  bottom.append(el("span", "pr-card__number", "#" + pr.number));

  const time = el("span", "pr-card__time");
  time.append(
    el("span", "pr-card__verb", meta.verb),
    document.createTextNode(" "),
    el("time", "pr-card__ago", relativeTime(pr.stampedAt))
  );
  const timeEl = time.querySelector("time");
  timeEl.dateTime = pr.stampedAt;
  timeEl.title = absoluteTime(pr.stampedAt);
  bottom.append(time);

  if (typeof pr.additions === "number") bottom.append(buildDiff(pr));

  card.append(top, title, bottom, el("span", "pr-card__edge"));
  return card;
}

function renderList(list, pullRequests) {
  list.setAttribute("aria-busy", "false");
  list.replaceChildren();
  pullRequests.forEach((pr, index) => {
    const card = renderCard(pr);
    card.style.setProperty("--pr-index", index);
    list.append(card);
  });
}

function setStatus(root, { mode, fetchedAt }) {
  const dot = root.querySelector(SELECTORS.liveDot);
  const label = root.querySelector(SELECTORS.liveLabel);
  const refreshed = root.querySelector(SELECTORS.refreshed);

  if (dot) dot.dataset.mode = mode;
  if (label) {
    label.textContent =
      mode === "live"
        ? "Live from GitHub"
        : mode === "cached"
        ? "Cached"
        : mode === "error"
        ? "Unavailable"
        : "Connecting";
  }
  if (!refreshed) return;

  if (!fetchedAt) {
    refreshed.textContent = "";
    refreshed.removeAttribute("title");
    return;
  }
  const iso = new Date(fetchedAt).toISOString();
  refreshed.textContent = "refreshed " + relativeTime(iso);
  refreshed.title = absoluteTime(iso);
}

/* ------------------------------------------------------------- controller */

export default function initGithubPullRequests() {
  const root = document.querySelector(SELECTORS.root);
  if (!root) return;

  const list = root.querySelector(SELECTORS.list);
  if (!list) return;

  const viewAll = root.querySelector(SELECTORS.viewAll);
  if (viewAll) viewAll.href = searchPageUrl();

  let lastFetchedAt = null;
  let statusMode = "loading";
  let inFlight = false;

  const paint = (mode) => {
    statusMode = mode;
    setStatus(root, { mode, fetchedAt: lastFetchedAt });
  };

  const showCached = (cached) => {
    renderList(list, cached.items);
    lastFetchedAt = cached.fetchedAt;
    paint(isFresh(cached) ? "live" : "cached");
  };

  async function load() {
    if (inFlight) return;
    inFlight = true;

    const cached = readCache();

    // Paint something immediately: a usable cache beats a spinner.
    if (cached && cached.items.length && isUsable(cached)) {
      showCached(cached);
      if (isFresh(cached)) {
        inFlight = false;
        return;
      }
    } else {
      renderSkeleton(list);
      paint("loading");
    }

    try {
      const pullRequests = await fetchPullRequests();

      if (!pullRequests.length) {
        renderMessage(list, {
          text: "No public pull requests to show right now.",
          linkLabel: "Visit GitHub profile",
          linkHref: profileUrl(),
        });
        lastFetchedAt = Date.now();
        paint("live");
        return;
      }

      // Render immediately, then fold in diff stats when they land.
      renderList(list, pullRequests);
      lastFetchedAt = Date.now();
      paint("live");
      writeCache(pullRequests);

      await attachDiffStats(pullRequests);
      if (pullRequests.some((pr) => typeof pr.additions === "number")) {
        applyDiffStats(list, pullRequests);
        writeCache(pullRequests);
      }
    } catch (err) {
      // Stale cache is a better answer than an error.
      if (cached && cached.items.length && isUsable(cached)) {
        showCached(cached);
        paint("cached");
        return;
      }
      renderMessage(list, {
        text:
          err && err.code === "rate-limit"
            ? "GitHub is rate limiting this request. Recent pull requests are on the profile."
            : "Couldn't reach GitHub just now. Recent pull requests are on the profile.",
        linkLabel: "Open GitHub profile",
        linkHref: profileUrl(),
        tone: "error",
      });
      paint("error");
    } finally {
      inFlight = false;
    }
  }

  load();

  // Keep "refreshed Xm ago" honest without re-hitting the API.
  setInterval(
    () => setStatus(root, { mode: statusMode, fetchedAt: lastFetchedAt }),
    30000
  );

  // Refresh on return to the tab once the cache has gone stale.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") return;
    const cached = readCache();
    if (!cached || !isFresh(cached)) load();
  });
}
